import { useEffect, useState, useMemo } from 'react';
import { Vendor, MSA, SOW, BillingEntry, ALL_CATEGORIES } from '../types';
import { vendorStore, msaStore, sowStore, billingStore } from '../services/store';

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function daysUntil(dateStr: string): number {
  return Math.floor((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
}

// ─── Mini bar ─────────────────────────────────────────────────────────────────
function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="bg-gray-100 rounded h-2 w-full">
      <div className="h-2 rounded transition-all" style={{ background: color, width: `${Math.min(Math.max(pct, 0), 100)}%` }} />
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 mb-5">
      <h2 className="text-sm font-semibold text-gray-700 mb-4">{title}</h2>
      {children}
    </div>
  );
}

export default function ReportsPage() {
  const [vendors,  setVendors]  = useState<Vendor[]>([]);
  const [msas,     setMsas]     = useState<MSA[]>([]);
  const [sows,     setSows]     = useState<SOW[]>([]);
  const [billing,  setBilling]  = useState<BillingEntry[]>([]);

  useEffect(() => {
    setVendors(vendorStore.list());
    setMsas(msaStore.list());
    setSows(sowStore.list());
    setBilling(billingStore.list());
  }, []);

  // ── 1. Vendor counts by category ──────────────────────────────────────────
  const byCategory = useMemo(() => {
    return ALL_CATEGORIES.map(cat => ({
      category: cat,
      total:    vendors.filter(v => v.categories.includes(cat)).length,
      active:   vendors.filter(v => v.categories.includes(cat) && v.status === 'Active').length,
    }));
  }, [vendors]);

  const maxCount = Math.max(...byCategory.map(c => c.total), 1);

  // ── 2. Upcoming expirations ───────────────────────────────────────────────
  type ExpiringItem = { label: string; vendorName: string; days: number; type: 'MSA' | 'SOW' };
  const expirations = useMemo((): ExpiringItem[] => {
    const vendorMap = new Map(vendors.map(v => [v.id, v.name]));
    const items: ExpiringItem[] = [];

    msas.forEach(m => {
      if (!m.expirationDate || m.authStatus !== 'Executed') return;
      const days = daysUntil(m.expirationDate);
      if (days >= 0 && days <= 365) {
        items.push({ label: `MSA (eff. ${m.effectiveDate ?? 'TBD'})`, vendorName: vendorMap.get(m.vendorId) ?? '?', days, type: 'MSA' });
      }
    });

    sows.forEach(s => {
      if (!s.endDate || s.authStatus !== 'Executed') return;
      const days = daysUntil(s.endDate);
      if (days >= 0 && days <= 365) {
        items.push({ label: `${s.sowNumber} — ${s.name}`, vendorName: vendorMap.get(s.vendorId) ?? '?', days, type: 'SOW' });
      }
    });

    return items.sort((a, b) => a.days - b.days);
  }, [msas, sows, vendors]);

  // ── 3. Total spend per vendor ─────────────────────────────────────────────
  const spendByVendor = useMemo(() => {
    const approved = billing.filter(e => e.authStatus === 'Approved');
    const spendMap = new Map<string, number>();
    approved.forEach(e => spendMap.set(e.vendorId, (spendMap.get(e.vendorId) ?? 0) + e.amount));
    return vendors
      .filter(v => (spendMap.get(v.id) ?? 0) > 0)
      .map(v => ({ vendor: v, spend: spendMap.get(v.id) ?? 0 }))
      .sort((a, b) => b.spend - a.spend);
  }, [vendors, billing]);

  // Spend by category
  const spendByCategory = useMemo(() => {
    const approved = billing.filter(e => e.authStatus === 'Approved');
    return ALL_CATEGORIES.map(cat => {
      const catVendorIds = new Set(vendors.filter(v => v.categories.includes(cat)).map(v => v.id));
      const spend = approved.filter(e => catVendorIds.has(e.vendorId)).reduce((s, e) => s + e.amount, 0);
      return { category: cat, spend };
    }).filter(c => c.spend > 0).sort((a, b) => b.spend - a.spend);
  }, [vendors, billing]);

  const maxSpend = Math.max(...spendByVendor.map(s => s.spend), 1);

  // ── 4. Budget vs. actual ──────────────────────────────────────────────────
  type BudgetRow = {
    label: string; vendorName: string; budget: number; spent: number;
    variance: number; overBudget: boolean; isCeiling: boolean;
  };
  const budgetVsActual = useMemo((): BudgetRow[] => {
    const rows: BudgetRow[] = [];

    // Vendor-level budgets
    vendors.forEach(v => {
      if (!v.budget) return;
      const spent = v.totalSpend;
      const budget = v.budget.amount;
      const threshold = v.spendThreshold;
      rows.push({
        label:       `${v.name} (Vendor budget)`,
        vendorName:  v.name,
        budget,
        spent,
        variance:    budget - spent,
        overBudget:  spent > budget,
        isCeiling:   threshold?.type === 'Ceiling',
      });
    });

    // SOW-level budgets
    const approved = billing.filter(e => e.authStatus === 'Approved');
    sows.forEach(s => {
      if (!s.budget) return;
      const vendorName = vendors.find(v => v.id === s.vendorId)?.name ?? '?';
      const spent = approved.filter(e => e.sowId === s.id).reduce((sum, e) => sum + e.amount, 0);
      const budget = s.budget.amount;
      rows.push({
        label:      `${s.sowNumber} — ${s.name}`,
        vendorName,
        budget,
        spent,
        variance:   budget - spent,
        overBudget: spent > budget,
        isCeiling:  false,
      });
    });

    return rows.sort((a, b) => (b.spent / b.budget) - (a.spent / a.budget));
  }, [vendors, sows, billing]);

  return (
    <div className="p-8">
      <h1 className="text-[22px] font-semibold text-gray-900 mb-1">Reports</h1>
      <p className="text-sm text-gray-500 mb-6">All data is live from the current session.</p>

      {/* ── 1. Vendor counts by category ── */}
      <Section title="Vendor count by category">
        {vendors.length === 0 ? (
          <p className="text-sm text-gray-400">No vendors yet.</p>
        ) : (
          <div className="space-y-3">
            {byCategory.map(row => (
              <div key={row.category}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700">{row.category}</span>
                  <span className="text-gray-500">{row.active} active / {row.total} total</span>
                </div>
                <Bar pct={(row.total / maxCount) * 100} color="#1a5c2a" />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ── 2. Upcoming expirations ── */}
      <Section title="Upcoming expirations (next 12 months)">
        {expirations.length === 0 ? (
          <p className="text-sm text-gray-400">No executed contracts expiring in the next 12 months.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="text-left pb-2 pr-4">Contract</th>
                <th className="text-left pb-2 pr-4">Vendor</th>
                <th className="text-left pb-2 pr-4">Type</th>
                <th className="text-right pb-2">Days remaining</th>
              </tr>
            </thead>
            <tbody>
              {expirations.map((item, i) => (
                <tr key={i} className="border-b border-gray-50 last:border-0">
                  <td className="py-2 pr-4 font-medium text-gray-800">{item.label}</td>
                  <td className="py-2 pr-4 text-gray-600">{item.vendorName}</td>
                  <td className="py-2 pr-4">
                    <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${item.type === 'MSA' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {item.type}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <span className={`font-semibold ${item.days <= 30 ? 'text-[#A32D2D]' : item.days <= 60 ? 'text-[#854F0B]' : 'text-gray-700'}`}>
                      {item.days}d
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* ── 3. Total spend ── */}
      <div className="grid grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Spend by vendor</h2>
          {spendByVendor.length === 0 ? (
            <p className="text-sm text-gray-400">No approved billing entries yet.</p>
          ) : (
            <div className="space-y-3">
              {spendByVendor.map(row => (
                <div key={row.vendor.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700 truncate pr-2">{row.vendor.name}</span>
                    <span className="text-gray-600 font-medium shrink-0">{fmt(row.spend)}</span>
                  </div>
                  <Bar pct={(row.spend / maxSpend) * 100} color="#1e40af" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Spend by category</h2>
          {spendByCategory.length === 0 ? (
            <p className="text-sm text-gray-400">No approved billing entries yet.</p>
          ) : (
            <div className="space-y-3">
              {spendByCategory.map(row => (
                <div key={row.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{row.category}</span>
                    <span className="text-gray-600 font-medium">{fmt(row.spend)}</span>
                  </div>
                  <Bar pct={(row.spend / Math.max(...spendByCategory.map(c => c.spend))) * 100} color="#7c3aed" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── 4. Budget vs. actual ── */}
      <Section title="Budget vs. actual">
        {budgetVsActual.length === 0 ? (
          <p className="text-sm text-gray-400">No vendors or SOWs with budgets set.</p>
        ) : (
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="text-left pb-2 pr-4">Item</th>
                <th className="text-right pb-2 pr-4">Budget</th>
                <th className="text-right pb-2 pr-4">Spent</th>
                <th className="text-right pb-2 pr-4">Variance</th>
                <th className="text-right pb-2">% Used</th>
              </tr>
            </thead>
            <tbody>
              {budgetVsActual.map((row, i) => {
                const pct = row.budget > 0 ? Math.round((row.spent / row.budget) * 100) : 0;
                const overColor = row.overBudget ? '#A32D2D' : '#3B6D11';
                return (
                  <tr key={i} className="border-b border-gray-50 last:border-0">
                    <td className="py-2.5 pr-4">
                      <div className="font-medium text-gray-800 text-xs">{row.label}</div>
                      <div className="text-[11px] text-gray-400">{row.vendorName}{row.isCeiling ? ' · Ceiling' : ''}</div>
                    </td>
                    <td className="py-2.5 pr-4 text-right text-gray-600">{fmt(row.budget)}</td>
                    <td className="py-2.5 pr-4 text-right font-medium" style={{ color: row.overBudget ? '#A32D2D' : '#374151' }}>{fmt(row.spent)}</td>
                    <td className="py-2.5 pr-4 text-right font-semibold" style={{ color: overColor }}>
                      {row.overBudget ? '▲ ' : '▼ '}{fmt(Math.abs(row.variance))}
                    </td>
                    <td className="py-2.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16">
                          <Bar pct={pct} color={pct > 100 ? '#A32D2D' : pct > 80 ? '#854F0B' : '#1a5c2a'} />
                        </div>
                        <span className="text-xs font-medium w-8 text-right" style={{ color: pct > 100 ? '#A32D2D' : '#374151' }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
